/**
 * The Tutorial Center: the planning track, docked beside a live Planning
 * screen that is running on the trainee's own practice fleet.
 *
 * Docked rather than laid over the app. The predecessor's first version threw a
 * full-screen layer over a frozen map, so a trainee read a lesson, closed it,
 * forgot the third step, and opened it again. Here the steps stay in view while
 * the real screen does the work, and each lesson ticks itself when the stores
 * the screen writes show the work done (see lessons.ts).
 */

import {
  Ban,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Crosshair,
  GraduationCap,
  Grid3x3,
  LogOut,
  MousePointerClick,
  RotateCcw,
  Route,
  Ship,
  Upload,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { usePlanDock } from '@/features/planning/planDocks';
import { cn } from '@/lib/utils';
import { useDroneIdentity, useSelectedDroneIds } from '@/stores/droneStore';
import { useCarrierPlans } from '@/stores/carrierStore';
import { useMissionStore, useZones } from '@/stores/missionStore';
import { useTargetingTarget } from '@/stores/targetingStore';

import { LESSONS, observedLessons, type LessonIcon, type LessonId } from './lessons';
import { usePracticeIds, usePracticeStore } from './practiceStore';
import { useTutorialStore } from './tutorialStore';

const OK = 'var(--ok)';
const CAUTION = 'var(--caution)';

const ICONS: Record<LessonIcon, LucideIcon> = {
  select: MousePointerClick,
  route: Route,
  pattern: Grid3x3,
  zone: Ban,
  target: Crosshair,
  upload: Upload,
  carrier: Ship,
};

/** Watches the planning stores and ticks off what the trainee has done. */
function useLessonWatcher(): void {
  const practiceIds = usePracticeIds();
  const selectedIds = useSelectedDroneIds();
  const drafts = useMissionStore((state) => state.draft);
  const uploaded = useMissionStore((state) => state.uploaded);
  const zones = useZones();
  const target = useTargetingTarget();
  const carrierPlans = useCarrierPlans();
  const patternGenerated = useTutorialStore((state) => state.patternGenerated);
  const zoneDone = useTutorialStore((state) => state.zones.done);
  const observeZones = useTutorialStore((state) => state.observeZones);
  const complete = useTutorialStore((state) => state.complete);

  const open = practiceIds.length > 0;
  useEffect(() => {
    if (open) observeZones(zones.map((zone) => zone.zoneId));
  }, [open, zones, observeZones]);

  useEffect(() => {
    complete(
      observedLessons({
        practiceIds: new Set(practiceIds),
        selectedIds,
        drafts,
        uploaded,
        targetPlaced: open && target !== null,
        patternGenerated: open && patternGenerated,
        zoneDrawnAndRemoved: zoneDone,
        carrierPlans,
      }),
    );
  }, [practiceIds, selectedIds, drafts, uploaded, target, patternGenerated, zoneDone, carrierPlans, open, complete]);
}

function PracticeAircraft({ droneId }: { droneId: string }) {
  const identity = useDroneIdentity(droneId);
  return (
    <span className="tnum rounded-full border border-white/10 bg-white/5 px-2 py-[2px] font-mono text-[9.5px] text-white/70">
      {identity?.callsign ?? droneId}
    </span>
  );
}

function PracticeStatusBlock({ onRetry, onEnd }: { onRetry: () => void; onEnd: () => void }) {
  const status = usePracticeStore((state) => state.status);
  const error = usePracticeStore((state) => state.error);
  const ids = usePracticeIds();

  return (
    <div className="rounded-[14px] border px-3 py-2.5" style={{ borderColor: `color-mix(in oklch, ${CAUTION} 35%, transparent)` }}>
      <div className="flex items-center gap-2">
        <span className="label-mono text-[8.5px]" style={{ color: CAUTION }}>
          PRACTICE FLEET
        </span>
        {status === 'open' ? (
          <button
            type="button"
            onClick={onEnd}
            className="ml-auto flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-[3px] font-mono text-[8.5px] font-bold text-white/70 hover:text-white"
          >
            <LogOut size={10} aria-hidden /> END PRACTICE
          </button>
        ) : null}
      </div>
      {status === 'opening' ? (
        <p className="mt-1.5 text-[10.5px] leading-relaxed text-white/50">
          Putting two quads and a boat on the map where you were looking.
        </p>
      ) : null}
      {status === 'open' ? (
        <>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {ids.map((id) => (
              <PracticeAircraft key={id} droneId={id} />
            ))}
          </div>
          <p className="mt-1.5 text-[10px] leading-relaxed text-white/45">
            Only you can see these. They are removed when you leave this screen.
          </p>
        </>
      ) : null}
      {status === 'refused' ? (
        <>
          <p className="mt-1.5 text-[10.5px] leading-relaxed" style={{ color: CAUTION }}>
            {error ?? 'The practice fleet could not be started.'}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded-full border border-white/15 px-2.5 py-[3px] font-mono text-[8.5px] font-bold text-white/70 hover:text-white"
          >
            TRY AGAIN
          </button>
        </>
      ) : null}
    </div>
  );
}

export function TutorialCenter({
  onRetry,
  onEnd,
  collapsible = false,
}: {
  onRetry: () => void;
  onEnd: () => void;
  /** Phone and tablet: the panel covers the map it teaches, so it can fold
   *  down to a pill while the trainee works the step. */
  collapsible?: boolean;
}) {
  useLessonWatcher();
  const { open, setOpen } = usePlanDock('tutorial');
  const folded = !open;
  const done = useTutorialStore((state) => state.done);
  const reset = useTutorialStore((state) => state.reset);
  const doneSet = useMemo(() => new Set(done), [done]);
  const firstOpen = LESSONS.find((lesson) => !doneSet.has(lesson.id))?.id ?? null;
  const [expanded, setExpanded] = useState<LessonId | null>(firstOpen);
  const count = LESSONS.filter((lesson) => doneSet.has(lesson.id)).length;
  const pct = Math.round((count / LESSONS.length) * 100);

  // Move on to the next lesson when the open one ticks itself, so the steps in
  // view are always the ones still to do.
  useEffect(() => {
    if (expanded !== null && doneSet.has(expanded)) setExpanded(firstOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneSet]);

  if (collapsible && folded) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open the Tutorial Center, ${count} of ${LESSONS.length} lessons done`}
        className="tutorial-pill glass-pill hit-44 pointer-events-auto absolute z-30 flex items-center gap-2 rounded-full border px-3 py-2 font-mono text-[9px] font-bold tracking-wide"
        style={{ borderColor: `color-mix(in oklch, ${CAUTION} 55%, transparent)` }}
      >
        <span
          className="rounded-full px-1.5 py-px text-[8px]"
          style={{ background: CAUTION, color: '#1c1503' }}
        >
          PRACTICE
        </span>
        <span className="text-white/75">
          TUTORIAL {count} OF {LESSONS.length}
        </span>
      </button>
    );
  }

  return (
    <aside
      aria-label="Tutorial Center"
      className="tutorial-center glass pointer-events-auto absolute right-[14px] top-[86px] bottom-[14px] z-20 flex w-[calc(var(--tutorial-col)-28px)] flex-col overflow-hidden rounded-[20px]"
    >
      <div className="flex flex-none flex-col gap-2.5 px-4 pt-3.5 pb-3">
        <div className="flex items-center gap-2.5">
          <span
            className="flex size-8 flex-none items-center justify-center rounded-full"
            style={{ background: `color-mix(in oklch, ${CAUTION} 18%, transparent)`, color: CAUTION }}
          >
            <GraduationCap size={16} aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="label-mono text-[8.5px] text-white/45">TUTORIAL CENTER</div>
            <div className="text-[14px] font-semibold leading-tight">Mission planning</div>
          </div>
          {count > 0 ? (
            <button
              type="button"
              onClick={reset}
              title="Clear your progress and start the track again"
              className="ml-auto flex items-center gap-1 rounded-full px-2 py-[3px] font-mono text-[8.5px] text-white/40 hover:bg-white/10 hover:text-white"
            >
              <RotateCcw size={10} aria-hidden /> RESET
            </button>
          ) : null}
          {collapsible ? (
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fold the Tutorial Center away"
              className={cn(
                'hit-44 rounded-full px-2 py-[3px] font-mono text-[8.5px] text-white/45 hover:bg-white/10 hover:text-white',
                count > 0 ? 'ml-1' : 'ml-auto',
              )}
            >
              HIDE
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-2.5">
          <div className="h-[5px] flex-1 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{ width: `${pct}%`, background: OK }}
            />
          </div>
          <span className="tnum font-mono text-[9.5px] text-white/55">
            {count} OF {LESSONS.length}
          </span>
        </div>

        <PracticeStatusBlock onRetry={onRetry} onEnd={onEnd} />
      </div>

      <ol className="scroll-thin flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-3 pb-3">
        {LESSONS.map((lesson, index) => {
          const isDone = doneSet.has(lesson.id);
          const isOpen = expanded === lesson.id;
          const Icon = ICONS[lesson.icon];
          return (
            <li
              key={lesson.id}
              data-testid={`lesson-${lesson.id}`}
              data-done={isDone ? 'true' : 'false'}
              className="rounded-[14px] border"
              style={{
                borderColor: isOpen ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)',
                background: isOpen ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.025)',
              }}
            >
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setExpanded(isOpen ? null : lesson.id)}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left"
              >
                <span className="flex-none" aria-label={isDone ? 'Done' : 'Not done yet'}>
                  {isDone ? (
                    <Check size={15} style={{ color: OK }} />
                  ) : (
                    <Circle size={15} className="text-white/25" />
                  )}
                </span>
                <Icon size={14} className="flex-none text-white/45" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-1.5">
                    <span className="tnum font-mono text-[9.5px] text-white/35">{index + 1}</span>
                    <span
                      className="truncate text-[12px] font-semibold"
                      style={{ color: isDone ? 'rgba(244,244,245,0.6)' : undefined }}
                    >
                      {lesson.title}
                    </span>
                  </span>
                  {!isOpen ? (
                    <span className="block truncate text-[10px] text-white/40">{lesson.outcome}</span>
                  ) : null}
                </span>
                {isOpen ? (
                  <ChevronDown size={14} className="flex-none text-white/40" aria-hidden />
                ) : (
                  <ChevronRight size={14} className="flex-none text-white/40" aria-hidden />
                )}
              </button>
              {isOpen ? (
                <div className="px-3 pb-3">
                  <p className="mb-2 text-[11px] leading-relaxed text-white/60">{lesson.outcome}</p>
                  <ol className="flex list-decimal flex-col gap-1.5 pl-4 text-[11px] leading-relaxed text-white/80 marker:font-mono marker:text-white/35">
                    {lesson.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                  <p
                    className="mt-2.5 text-[10px] leading-relaxed"
                    style={{ color: isDone ? OK : 'rgba(244,244,245,0.4)' }}
                  >
                    {isDone ? 'Done.' : lesson.check}
                  </p>
                </div>
              ) : null}
            </li>
          );
        })}
        {count === LESSONS.length ? (
          <li
            className="mt-1 flex items-center gap-2 rounded-[14px] border px-3 py-2.5 text-[11.5px]"
            style={{ borderColor: `color-mix(in oklch, ${OK} 40%, transparent)`, color: OK }}
          >
            <GraduationCap size={15} aria-hidden />
            You have planned a full mission, zones and a carrier launch included.
          </li>
        ) : null}
      </ol>
    </aside>
  );
}

export default TutorialCenter;
